import React, { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../../../hooks";
import { TeacherAPI } from "../../../../api";
import { TeacherEvaluationGrid } from "../../../../components/Admin/EvaluationRutasComp/TeacherEvaluationGrid";
import { PageHeader } from "../../../../ui";
import { useThemeRuntime } from "../../../../ui/theme/ThemeRuntimeProvider";

import paso1 from "../../../../assets/illustrations/guia-evaluacion/paso1.png";
import paso2 from "../../../../assets/illustrations/guia-evaluacion/paso2.png";
import paso3 from "../../../../assets/illustrations/guia-evaluacion/paso3.png";

import styles from "./Page_Teacher_evalucacion.module.scss";

const teacherController = new TeacherAPI();

export function Page_Teacher_evalucacion() {
  const { t } = useTranslation("teachers");
  const { user, accessToken } = useAuth();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);

  // Overrides del editor de estilos (guia rapida por pagina). En la sesion de un
  // diseñador ganan; para el resto, valor por defecto (i18n + asset).
  const { resolvedOverrides } = useThemeRuntime();
  const ov = (key, fallback) => {
    const v = resolvedOverrides[key];
    return typeof v === "string" && v.trim() ? v : fallback;
  };

  const headerSteps = useMemo(
    () => [
      {
        title: ov(
          "text.evaluacion.guide.step1.title",
          t("pages.evaluacionStep1"),
        ),
        description: ov(
          "text.evaluacion.guide.step1.desc",
          t("pages.evaluacionStep1Desc"),
        ),
        illustration: ov("image.evaluacion.guide.step1", paso1),
      },
      {
        title: ov(
          "text.evaluacion.guide.step2.title",
          t("pages.evaluacionStep2"),
        ),
        description: ov(
          "text.evaluacion.guide.step2.desc",
          t("pages.evaluacionStep2Desc"),
        ),
        illustration: ov("image.evaluacion.guide.step2", paso2),
      },
      {
        title: ov(
          "text.evaluacion.guide.step3.title",
          t("pages.evaluacionStep3"),
        ),
        description: ov(
          "text.evaluacion.guide.step3.desc",
          t("pages.evaluacionStep3Desc"),
        ),
        illustration: ov("image.evaluacion.guide.step3", paso3),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, resolvedOverrides],
  );
  const guideTitleOv = ov("text.evaluacion.guide.title", undefined);

  useEffect(() => {
    let active = true;

    const fetchTeacherData = async () => {
      try {
        const response = await teacherController.getTeacherById(
          accessToken,
          user?.user?.teacher,
        );
        if (!active) return;
        setTeacher(response.teacher);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error al obtener datos del docente:", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    if (user?.user?.teacher) fetchTeacherData();

    return () => {
      active = false;
    };
  }, [accessToken, user]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
        <p style={{ marginTop: 12, color: "#6b7280" }}>
          {t("pages.loadingTeacher")}
        </p>
      </div>
    );
  }

  if (!teacher) return null;

  return (
    <div className={`${styles.page} page-teacher-evaluacion`}>
      <PageHeader
        title={t("pages.evaluacionTitle")}
        description={t("pages.evaluacionDesc")}
        steps={headerSteps}
        guideTitle={guideTitleOv}
      />

      <TeacherEvaluationGrid teacher={teacher} accessToken={accessToken} />
    </div>
  );
}
